/**
 * Admin preview of a template package (JUB-161).
 *
 * Renders a validated or installed package with the real template engine
 * against the 16 scenario fixtures, in RO or EN, at phone, tablet or desktop
 * width. The page is drawn into a same-origin iframe so the package's own
 * breakpoints and fixed sections behave as they do on the public page; the
 * iframe is sandboxed without scripts and nothing in it reaches the network
 * beyond the stylesheets the app already loads. Interactions are inert.
 */

import {
  Component,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TemplateRenderer } from "@/lib/template-engine/TemplateRenderer";
import { stringsForLocale } from "@/lib/template-engine/loadPackage";
import { createMessageTranslator } from "@/lib/template-engine/messageTranslator";
import {
  buildScenarios,
  type Scenario,
  type ScenarioFile,
} from "@/lib/template-engine/scenarioFixtures";
import { fontScaleFor } from "@/lib/template-engine/tokens";
import {
  scenarioDescriptionKey,
  scenarioLabelKey,
} from "./templatePreviewScenarios";
import type {
  EngineContext,
  TemplatePackage,
} from "@/lib/template-engine/types";

export const PREVIEW_WIDTHS = {
  mobile: 375,
  tablet: 768,
  desktop: 1440,
} as const;
export type PreviewWidth = keyof typeof PREVIEW_WIDTHS;
type PreviewLocale = "ro" | "en";

const FRAME_HEIGHT = 900;

const noop = () => undefined;

let scenariosPromise: Promise<Scenario[]> | null = null;

/** The 16 fixtures, from the server-side copy the starter kit ships (kept equal to .specify by a test). */
function loadScenarios(): Promise<Scenario[]> {
  scenariosPromise ??=
    import("../../../../server/resources/jtpk/starter-kit/docs/scenarios.json?raw").then(
      (module) => buildScenarios(JSON.parse(module.default) as ScenarioFile),
    );
  return scenariosPromise;
}

class PreviewBoundary extends Component<
  { fallback: (message: string) => ReactNode; children: ReactNode },
  { error: string | null }
> {
  state = { error: null as string | null };

  static getDerivedStateFromError(error: unknown) {
    return { error: error instanceof Error ? error.message : String(error) };
  }

  render() {
    return this.state.error
      ? this.props.fallback(this.state.error)
      : this.props.children;
  }
}

function buildContext(
  pkg: TemplatePackage,
  scenario: Scenario,
  locale: PreviewLocale,
): EngineContext {
  return {
    event: scenario.event,
    locale,
    t: createMessageTranslator(locale),
    now: new Date(scenario.env.now),
    prefersReducedMotion: scenario.env.prefersReducedMotion,
    fontScale: fontScaleFor(scenario.event.fontScale),
    gifts: {
      items: scenario.gifts.gifts,
      categories: scenario.gifts.categories,
      activeCategory: "all",
      cardPaymentsEnabled: scenario.gifts.cardPaymentsEnabled,
      loading: false,
    },
    strings: stringsForLocale(pkg, locale),
    actions: {
      rsvpOpen: noop,
      giftContribute: noop,
      lightboxOpen: noop,
      filterSet: noop,
      share: noop,
      calendarGoogle: noop,
      calendarIcs: noop,
      scrollTo: noop,
      mapOpen: noop,
    },
  };
}

/** Same-origin iframe whose body hosts a React portal, with the app's stylesheets copied in. */
function PreviewFrame({
  width,
  locale,
  children,
}: {
  width: number;
  locale: string;
  children: ReactNode;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [body, setBody] = useState<HTMLElement | null>(null);
  const [scale, setScale] = useState(1);

  const attach = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    doc.documentElement.lang = locale;
    for (const node of document.head.querySelectorAll(
      'link[rel="stylesheet"], style',
    )) {
      doc.head.appendChild(node.cloneNode(true));
    }
    doc.body.style.margin = "0";
    setBody(doc.body);
  };

  useEffect(() => {
    const doc = frameRef.current?.contentDocument;
    if (doc) doc.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const update = () => setScale(Math.min(1, box.clientWidth / width));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(box);
    return () => observer.disconnect();
  }, [width]);

  // JUB-217: the measuring box spans the dialog, the framed preview is centred inside it.
  return (
    <div ref={boxRef} className="w-full">
      <div
        className="mx-auto overflow-hidden rounded-md border bg-muted/30"
        style={{ width: width * scale, height: FRAME_HEIGHT * scale }}
      >
        <iframe
          ref={frameRef}
          title="template-preview"
          data-testid="template-preview-frame"
          sandbox="allow-same-origin"
          srcDoc="<!doctype html><html><head><meta charset='utf-8'></head><body></body></html>"
          onLoad={attach}
          style={{
            width,
            height: FRAME_HEIGHT,
            border: 0,
            background: "white",
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        />
        {body && createPortal(children, body)}
      </div>
    </div>
  );
}

export type TemplatePreviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pkg: TemplatePackage | null;
  title: string;
};

export function TemplatePreviewDialog({
  open,
  onOpenChange,
  pkg,
  title,
}: TemplatePreviewDialogProps) {
  const { t, i18n } = useTranslation();
  const [scenarios, setScenarios] = useState<Scenario[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scenarioId, setScenarioId] = useState("wedding-full");
  // JUB-217: the preview starts in the administrator's own language.
  const [locale, setLocale] = useState<PreviewLocale>(i18n?.language?.startsWith("en") ? "en" : "ro");
  const [width, setWidth] = useState<PreviewWidth>("mobile");

  useEffect(() => {
    if (!open || scenarios) return;
    loadScenarios().then(setScenarios, (error: unknown) =>
      setLoadError(String(error)),
    );
  }, [open, scenarios]);

  const scenario = scenarios?.find((entry) => entry.id === scenarioId) ?? null;
  const ctx = useMemo(
    () => (pkg && scenario ? buildContext(pkg, scenario, locale) : null),
    [pkg, scenario, locale],
  );

  const selectClass =
    "h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(96vw,1200px)] max-w-none max-h-[94vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("admin.templates.preview.title", { name: title })}
          </DialogTitle>
          <DialogDescription>
            {t("admin.templates.preview.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3 py-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">
              {t("admin.templates.preview.scenario")}
            </span>
            <select
              data-testid="template-preview-scenario"
              className={selectClass}
              value={scenarioId}
              onChange={(event) => setScenarioId(event.target.value)}
              disabled={!scenarios}
            >
              {(scenarios ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {t(scenarioLabelKey(entry.id))}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">
              {t("admin.templates.preview.locale")}
            </span>
            <select
              data-testid="template-preview-locale"
              className={selectClass}
              value={locale}
              onChange={(event) =>
                setLocale(event.target.value as PreviewLocale)
              }
            >
              <option value="ro">RO</option>
              <option value="en">EN</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">
              {t("admin.templates.preview.width")}
            </span>
            <select
              data-testid="template-preview-width"
              className={selectClass}
              value={width}
              onChange={(event) => setWidth(event.target.value as PreviewWidth)}
            >
              {(Object.keys(PREVIEW_WIDTHS) as PreviewWidth[]).map((key) => (
                <option key={key} value={key}>
                  {t(`admin.templates.preview.widths.${key}`)} ·{" "}
                  {PREVIEW_WIDTHS[key]} px
                </option>
              ))}
            </select>
          </label>
        </div>

        {scenario && (
          <div className="space-y-0.5">
            <p
              className="text-xs text-muted-foreground"
              data-testid="template-preview-scenario-description"
            >
              {t(scenarioDescriptionKey(scenario.id))}
            </p>
            <p
              className="font-mono text-[11px] text-muted-foreground/70"
              data-testid="template-preview-scenario-id"
            >
              {t("admin.templates.preview.scenarioId", { id: scenario.id })}
            </p>
          </div>
        )}

        {loadError ? (
          <p className="text-sm text-destructive">
            {t("admin.templates.preview.error", { message: loadError })}
          </p>
        ) : !pkg || !ctx ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <PreviewFrame width={PREVIEW_WIDTHS[width]} locale={locale}>
            <PreviewBoundary
              key={`${scenarioId}-${locale}`}
              fallback={(message) => (
                <p
                  style={{
                    padding: 16,
                    fontFamily: "sans-serif",
                    color: "#b91c1c",
                  }}
                >
                  {t("admin.templates.preview.error", { message })}
                </p>
              )}
            >
              <TemplateRenderer pkg={pkg} ctx={ctx} />
            </PreviewBoundary>
          </PreviewFrame>
        )}
      </DialogContent>
    </Dialog>
  );
}
